import { ACTToolbarService } from 'src/app/angularJS/services/toolbar.service';
import { LinkBuilder } from 'src/app/angularJS/services/link-builder.service';
import { UserService } from 'src/app/angularJS/services/user/user.service';
import { DecodedItsoMessageTranslationRequestService } from 'src/app/angularJS/services/decoded-itso-message-translation-request/decoded-itso-message-translation-request.service';
import { DecodedItsoMessageService } from 'src/app/angularJS/services/decoded-itso-message/decoded-itso-message.service';
import { UsageEventService } from 'src/app/angularJS/services/usage-event/usage-event.service';
import { BreadcrumbService } from 'src/app/angularJS/services/breadcrumbs/breadcrumb.service';

/**
 * A controller that handles events related to uploading/adding itso messages into the system
 */
export class DecodedItsoMessageUploadController {
  public static $inject = [
    'ACTToolbarService',
    'LinkBuilder',
    'userService',
    '$state',
    '$q',
    'decodedItsoMessageTranslationRequestService',
    'decodedItsoMessageService',
    '$rootScope',
    'usageEventService',
    'breadcrumbService',
    'PrimaryAction'
  ];
  public uploadDetail;
  public translationToUsageEventRequests: Array<InterACT.Interfaces.UsageEvents.TranslationToUsageEventRequest> = [];
  public paginatorData: InterACT.Interfaces.IPaginationData = {
    currentPage: 1,
    perPageCount: 15,
    totalCount: 0
  };
  public decodedItsoMessageDictionary: InterACT.Interfaces.IDictionary<InterACT.Interfaces.ItsoMessages.DecodedItsoMessage> = {};
  public usageEventDictionary: InterACT.Interfaces.IDictionary<InterACT.Interfaces.UsageEvents.UsageEvent> = {};
  public usageEventCreatedDateDictionary: InterACT.Interfaces.IDictionary<string> = {};
  public showMoreFields = false;

  constructor(
    private ActToolbarService: ACTToolbarService,
    private linkBuilder: LinkBuilder,
    private userService: UserService,
    private $state: ng.ui.IStateService,
    private $q: ng.IQService,
    private decodedItsoMessageTranslationRequestService: DecodedItsoMessageTranslationRequestService,
    private decodedItsoMessageService: DecodedItsoMessageService,
    private $rootScope,
    private usageEventService: UsageEventService,
    private breadcrumbService: BreadcrumbService,
    private PrimaryAction: any
  ) {
    this.uploadDetail = {};
    this.activate();
  }

  /**
   * A method that gets invoked when a file has been imported into the client (not stored in the system, just available in the client)
   */
  public onFileImported = () => {
    const r = new FileReader();
    r.onload = (e: any) => {
      const contents = e.target.result;
      const decodedItsoMessages = this.createDecodedItsoMessagesFromCsvFile(contents);

      this.$state.go('decoded-itso-message-upload-list', { decodedItsoMessages: decodedItsoMessages });
    };

    r.readAsText(this.uploadDetail.file);
  };

  /**
   * A method that gets a page worth of decoded itso message translation requests for the supplied page
   * @param page the number of the page to get the requests for
   * @param perPage the amount of requests to get
   * @returns a promise that resolves once the operation is complete
   */
  public getDecodedItsoMessageTranslationRequests = (page: number, perPage: number) => {
    const deferred = this.$q.defer();

    if (!perPage) {
      perPage = this.paginatorData.perPageCount;
    }

    this.$rootScope.showSpinner = true;
    this.decodedItsoMessageTranslationRequestService.getList(page, perPage, 'scheme.link = \'' + this.linkBuilder.getSchemesLink(this.userService.selectedScheme.id) + '\' sortBy created desc').then((response) => {

      const allPromises = [];
      for (let c = 0; c < response.translationToUsageEventRequests.length; c++) {
        const req = response.translationToUsageEventRequests[c];
        const decodedItsoMessageId = req.translationToUsageEventRequest.translationSource.link.split('/')[2];
        allPromises.push(this.getDecodedItsoMessage(decodedItsoMessageId));

        if (req.translationToUsageEventRequest.usageEvent) {
          const usageEventId = req.translationToUsageEventRequest.usageEvent.link.split('/')[2];
          allPromises.push(this.getUsageEvent(usageEventId));
        }
      }

      this.$q.when(allPromises).then(() => {
        this.translationToUsageEventRequests = response.translationToUsageEventRequests.map((request) => { return request.translationToUsageEventRequest; });
        this.paginatorData.totalCount = response.meta.totalCount;
        this.$rootScope.showSpinner = false;
        deferred.resolve();
      }).catch((error) => {
        this.$rootScope.showSpinner = false;
      });
    });

    return deferred.promise;
  };

  /**
   * A method that retrieves a decoded itso message with a given id
   * @param itsoMessageId the id of the decoded itso message to retrieve
   * @returns a promise that resolves with the decoded itso message with the supplied id
   */
  private getDecodedItsoMessage(decodedItsoMessageId: string) {
    const deferred = this.$q.defer();

    if (this.decodedItsoMessageDictionary[decodedItsoMessageId]) {
      deferred.resolve();
    } else {
      this.decodedItsoMessageService.get(decodedItsoMessageId).then((response) => {
        this.decodedItsoMessageDictionary[decodedItsoMessageId] = response.decodedItsoMessage;
        deferred.resolve();
      }).catch((error) => {
        deferred.reject(error);
      });
    }

    return deferred.promise;
  }

  /**
   * A method that retrieves a usage event with a given id
   * @param usageEventId the id of the usage event to retrieve
   * @returns a promise that resolves with the usage evetn with the supplied id
   */
  private getUsageEvent(usageEventId: string) {
    const deferred = this.$q.defer();

    if (this.usageEventDictionary[usageEventId]) {
      deferred.resolve();
    } else {
      this.usageEventService.get(usageEventId).then((response) => {
        this.usageEventDictionary[usageEventId] = response.usageEvent;
        this.usageEventCreatedDateDictionary[usageEventId] = (<any>response.meta).created;
        deferred.resolve();
      }).catch((error) => {
        deferred.reject(error);
      });
    }

    return deferred.promise;
  }

  /**
   * Creates multiple decoded Itso Messages from from a csv string
   * @param csvFileContents the contents of a csv file that contains decoded itso message data
   */
  private createDecodedItsoMessagesFromCsvFile(csvFileContents: string) {
    const decodedItsoMessages: Array<InterACT.Interfaces.ItsoMessages.DecodedItsoMessage> = [];
    const decodedItsoMessageStrings = csvFileContents.split('\r\n');

    if (decodedItsoMessageStrings.length <= 1) {
      return [];
    }

    const decodedItsoMessagePropertyNames = decodedItsoMessageStrings[0].split(',');

    for (let c = 1; c < decodedItsoMessageStrings.length; c++) {
      const decodedItsoMessageString = decodedItsoMessageStrings[c];

      if (decodedItsoMessageString.length === 0) {
        continue;
      }

      const decodedItsoMessage = this.createDecodedItsoMessageFromString(decodedItsoMessageString, decodedItsoMessagePropertyNames);
      decodedItsoMessages.push(decodedItsoMessage);
    }

    return decodedItsoMessages;
  }

  /**
   * Creates a single decoded Itso message from a csv itso message string
   * @param itsoMessageString the csv string that contains the decoded itso message data
   * @param itsoMessageHeaders the headers that belong to the csv decoded itso message string
   */
  private createDecodedItsoMessageFromString(decodedItsoMessageString: string, decodedItsoMessageHeaders: Array<string>) {
    const decodedItsoMessageDetails = decodedItsoMessageString.split(',');

    const decodedItsoMessage: InterACT.Interfaces.ItsoMessages.DecodedItsoMessage = <any>{
      scheme: this.userService.convertSchemeToLink(this.userService.selectedScheme),
      messageFields: {}
    };

    for (let c = 0; c < decodedItsoMessageDetails.length; c++) {
      const dataItem = decodedItsoMessageDetails[c];
      const propertyName = decodedItsoMessageHeaders[c];

      if (dataItem) {
        if (propertyName.indexOf('.') === -1) {
          decodedItsoMessage.messageFields[propertyName] = dataItem;
        } else {
          const propertyPath = propertyName.split('.');

          let targetObject = decodedItsoMessage.messageFields;
          for (let i = 0; i < propertyPath.length; i++) {
            let nestedPropertyName = propertyPath[i];

            if (!targetObject[nestedPropertyName]) {
              if (i < propertyPath.length - 1) {
                targetObject[nestedPropertyName] = {};
                targetObject = targetObject[nestedPropertyName];
              } else {
                targetObject[nestedPropertyName] = dataItem;
              }
            } else {
              targetObject = targetObject[nestedPropertyName];
            }
          }
        }
      }
    }

    (<any>decodedItsoMessage).status = 'NOT_UPLOADED';

    return decodedItsoMessage;
  }

  /**
   * A method that directs the user to the decoded itso message upload list state
   */
  private goToDecodedItsoMessageUploadList = () => {
    this.$state.go('decoded-itso-message-upload-list');
  }

  /**
   * A method which is used to instantiate our controller
   */
  private activate() {
    this.breadcrumbService.setBreadcrumbs([{
      name: 'ITSO_MESSAGE_UPLOAD.ITSO_MESSAGE_UPLOAD',
      sref: 'decoded-itso-message-upload'
    }]);

    this.ActToolbarService.getToolbar('primary').then((toolbar) => {
      toolbar.setTitle('ITSO_MESSAGE_UPLOAD.ITSO_MESSAGE_UPLOAD');
    });

    this.PrimaryAction.setActions([{
      name: 'CREATE_JOURNEY_CONSTRUCTION_RULE',
      action: this.goToDecodedItsoMessageUploadList
    }]);

    this.getDecodedItsoMessageTranslationRequests(1, this.paginatorData.perPageCount);
  }
}